import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import type { BatchSummary } from '@shared/models/BatchSummary';
import { faCheck, faTimes, faLocationDot, faBox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveBatchIdForProduct, type ResolvedLocation, type ResolvedProduct } from '../../models';
import { ScannerInput } from './ScannerInput';

interface QuantityConfirmProps {
  selectedProduct: ResolvedProduct;
  selectedLocation: ResolvedLocation;
  onConfirm: (
    quantity: number,
  ) => { success: boolean; message: string } | Promise<{ success: boolean; message: string }>;
  onCancel: () => void;
}

export const QuantityConfirm: React.FC<QuantityConfirmProps> = ({
  selectedProduct,
  selectedLocation,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('relocationProcessModule');
  const [quantity, setQuantity] = useState(1);
  const [isConfirming, setIsConfirming] = useState(false);

  const productCode = selectedProduct.code?.trim() ?? '';
  const productName = selectedProduct.name?.trim() ?? '';
  const displayName = productName || t('quantity_confirm.product_name_fallback');
  /** Texto para placeholder del escáner: código preferido, si no el nombre (nunca el id en UI). */
  const scanHintLabel = productCode || productName;

  const stockBatchEntry = useMemo(() => {
    const batches = selectedLocation.stock_batches;
    if (!batches?.length) return undefined;
    const bid = resolveBatchIdForProduct(batches, selectedProduct.id);
    if (bid == null) return undefined;
    return batches.find((s) => s.batch_id === bid);
  }, [selectedLocation.stock_batches, selectedProduct.id]);

  const batchSummary: BatchSummary | undefined = useMemo(() => {
    const b = stockBatchEntry;
    if (!b) return undefined;
    const nested = b.batch;
    return {
      id: b.batch_id,
      batch_number: nested?.batch_number ?? undefined,
      expiry_date: nested?.expiry_date ?? undefined,
    };
  }, [stockBatchEntry]);

  const handleScan = (scannedCode: string) => {
    const trimmed = scannedCode.trim();
    const expected = selectedProduct.code?.trim() || String(selectedProduct.id);
    if (trimmed.toLowerCase() === expected.toLowerCase()) {
      const newQty = quantity + 1;
      setQuantity(newQty);
      return { success: true, message: `Cantidad incrementada a ${newQty}` };
    }
    return { success: false, message: 'Código incorrecto. Escanea el producto seleccionado' };
  };

  const handleConfirm = async () => {
    if (quantity <= 0) return;
    setIsConfirming(true);
    try {
      await onConfirm(quantity);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(Math.max(1, newQuantity));
  };

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{t('quantity_confirm.title')}</h3>
        <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-sm font-medium">
          {selectedLocation.code}
        </div>
      </div>

      <div className="mb-4">
        <Product
          id={selectedProduct.id}
          src={selectedProduct.image || ''}
          name={displayName}
          code={productCode || undefined}
          acronym={displayName.charAt(0).toUpperCase()}
          showImageModal={true}
          enableLineClamp={true}
        />
        {batchSummary && (batchSummary.batch_number?.trim() || batchSummary.expiry_date) ? (
          <LineBatchMeta
            className="mt-3"
            batch={batchSummary}
            batchId={stockBatchEntry?.batch_id ?? null}
            hideNumericIdFallback
            i18nNs="relocationProcessModule"
            lotLabelKey="panel.batch"
            expiryLabelKey="panel.expiry"
          />
        ) : null}
      </div>

      <div className="mb-4">
        <ScannerInput
          currentStep="product"
          onScan={handleScan}
          placeholder={
            scanHintLabel
              ? t('quantity_confirm.scan_hint', { code: scanHintLabel })
              : t('quantity_confirm.scan_hint_no_code')
          }
        />
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">{t('quantity_confirm.qty_label')}</span>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <StepperInput
              value={quantity}
              onChange={handleQuantityChange}
              min={1}
              step={1}
              allowNegative={false}
              data-testid="relocation-quantity-stepper"
            />
          </div>
        </div>
      </div>

      {/* Resumen "línea candidata" antes de enviar */}
      <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
        <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
          {t('quantity_confirm.summary_title')}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-foreground items-baseline">
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faLocationDot} className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{t('quantity_confirm.from_label')}:</span>
            <span className="font-medium">{selectedLocation.code}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FontAwesomeIcon icon={faBox} className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{t('quantity_confirm.product_code_label')}:</span>
            <span className="font-medium">
              {productCode || (
                <span className="text-muted-foreground font-normal">{t('quantity_confirm.no_product_code')}</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground">{t('quantity_confirm.qty_label')}:</span>
            <span className="font-semibold">{quantity}</span>
          </div>
        </div>
        {batchSummary && (batchSummary.batch_number?.trim() || batchSummary.expiry_date) ? (
          <LineBatchMeta
            className="mt-3"
            batch={batchSummary}
            batchId={stockBatchEntry?.batch_id ?? null}
            hideNumericIdFallback
            i18nNs="relocationProcessModule"
            lotLabelKey="panel.batch"
            expiryLabelKey="panel.expiry"
          />
        ) : null}
      </div>

      <div className="flex space-x-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isConfirming}
          className="flex-1 py-2 px-3 border border-border rounded font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
          {t('quantity_confirm.cancel')}
        </button>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirming || quantity <= 0}
          className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded font-medium focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isConfirming ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('quantity_confirm.confirming')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
              {t('quantity_confirm.confirm')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};
